import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import StarRating from './TestimonialStars';
import WebsitePreview from './TestimonialPreview';
import { EMPTY_TESTIMONIAL_FORM, testimonialToForm } from './testimonialUtils';

const SOURCES = ['WhatsApp', 'Google', 'Phone Call', 'Direct', 'Other'];
const MAX_REVIEW = 500;

const TestimonialModal = ({ open, onOpenChange, editing, initialValues, onSaved }) => {
  const [form, setForm] = useState(EMPTY_TESTIMONIAL_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm(testimonialToForm(editing));
    } else if (initialValues) {
      setForm({ ...EMPTY_TESTIMONIAL_FORM, ...initialValues });
    } else {
      setForm(EMPTY_TESTIMONIAL_FORM);
    }
  }, [open, editing, initialValues]);

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const save = async () => {
    if (!form.customer_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (!form.review_text.trim()) {
      toast.error('Review text is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        route: form.route.trim() || null,
        travel_month: form.travel_month.trim() || null,
        review_text: form.review_text.trim(),
        rating: form.rating,
        source: form.source,
        photo_url: form.photo_url.trim() || null,
        is_featured: form.is_featured,
        is_active: form.is_active,
        display_order: Number(form.display_order) || 0,
      };
      if (editing) {
        const { error } = await supabase.from('testimonials').update(payload).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('testimonials')
          .insert({ ...payload, id: crypto.randomUUID(), created_at: new Date().toISOString() });
        if (error) throw error;
      }
      toast.success('✓ Testimonial saved');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.message ? `Failed to save testimonial — ${err.message}` : 'Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="font-body max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-[#1B2D5E]">
            {editing ? 'Edit Testimonial' : 'Add Testimonial'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <Label>Customer Name *</Label>
            <Input
              className="mt-1"
              placeholder="e.g. Priyesh Shah"
              value={form.customer_name}
              onChange={(e) => set('customer_name', e.target.value)}
            />
          </div>

          <div>
            <Label>Route</Label>
            <Input
              className="mt-1"
              placeholder="e.g. Rajkot → Mumbai"
              value={form.route}
              onChange={(e) => set('route', e.target.value)}
            />
            <p className="text-[11px] text-[#888] mt-1">Shows below customer name</p>
          </div>

          <div>
            <Label>Travel Month</Label>
            <Input
              className="mt-1"
              placeholder="e.g. May 2025"
              value={form.travel_month}
              onChange={(e) => set('travel_month', e.target.value)}
            />
          </div>

          <div>
            <Label>Rating *</Label>
            <div className="mt-2">
              <StarRating value={form.rating} onChange={(n) => set('rating', n)} size={28} />
            </div>
            <p className="text-[12px] text-[#888] mt-1">{form.rating} out of 5 stars</p>
          </div>

          <div>
            <Label>Review Text *</Label>
            <Textarea
              className="mt-1"
              rows={5}
              placeholder="Write the customer's review here..."
              value={form.review_text}
              maxLength={MAX_REVIEW}
              onChange={(e) => set('review_text', e.target.value)}
            />
            <p className="text-[12px] text-[#888] mt-1 text-right">
              {form.review_text.length}/{MAX_REVIEW}
            </p>
          </div>

          <div>
            <Label>Source</Label>
            <Select value={form.source} onValueChange={(v) => set('source', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Photo URL (optional)</Label>
            <div className="flex items-center gap-3 mt-1">
              <Input
                placeholder="https://..."
                value={form.photo_url}
                onChange={(e) => set('photo_url', e.target.value)}
              />
              {form.photo_url && (
                <img
                  src={form.photo_url}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover shrink-0 border border-[#EBEBEB]"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
            </div>
            <p className="text-[11px] text-[#888] mt-1">Customer profile photo (optional)</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Featured</Label>
                <Switch checked={form.is_featured} onCheckedChange={(v) => set('is_featured', v)} />
              </div>
              <p className="text-[11px] text-[#888]">Show as pull-quote in Why Choose Us</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch checked={form.is_active} onCheckedChange={(v) => set('is_active', v)} />
              </div>
              <p className="text-[11px] text-[#888]">Toggle to hide/show without deleting</p>
            </div>
          </div>

          <div>
            <Label>Display Order</Label>
            <Input
              type="number"
              min="0"
              className="mt-1 w-32"
              value={form.display_order}
              onChange={(e) => set('display_order', e.target.value)}
            />
            <p className="text-[11px] text-[#888] mt-1">Lower number shown first (0, 1, 2…)</p>
          </div>

          <WebsitePreview testimonial={form} />

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#1B2D5E] hover:bg-[#E8651A] text-white rounded-lg px-7 py-3"
            >
              {saving ? 'Saving…' : 'Save Testimonial'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TestimonialModal;
